/**
 * Time Constants for Tee Sheet Management
 *
 * Defines structured time request types and conversions from
 * vague terms (early, middle, late) to actual time ranges.
 */

// Time request types enum values
export const TIME_REQUEST_TYPES = {
  EXACT: 'exact',      // Specific time (e.g., "8:30 AM")
  BEFORE: 'before',    // Before a specific time
  AFTER: 'after',      // After a specific time
  BETWEEN: 'between',  // Between two times (range)
  NONE: 'none'         // No preference
};

// Standard time ranges for converting vague requests
export const TIME_RANGES = {
  EARLY: {
    label: 'Early',
    start: '07:00',
    end: '08:30',
    description: '7:00 AM - 8:30 AM'
  },
  MIDDLE: {
    label: 'Middle',
    start: '08:30',
    end: '10:00',
    description: '8:30 AM - 10:00 AM'
  },
  LATE: {
    label: 'Late',
    start: '10:00',
    end: null,  // No end time - just after 10
    description: 'After 10:00 AM'
  }
};

// Preset time options for the selector
export const TIME_PRESETS = [
  { value: '07:00', label: '7:00 AM' },
  { value: '07:30', label: '7:30 AM' },
  { value: '08:00', label: '8:00 AM' },
  { value: '08:30', label: '8:30 AM' },
  { value: '09:00', label: '9:00 AM' },
  { value: '09:30', label: '9:30 AM' },
  { value: '10:00', label: '10:00 AM' },
  { value: '10:30', label: '10:30 AM' },
  { value: '11:00', label: '11:00 AM' },
  { value: '11:30', label: '11:30 AM' },
  { value: '12:00', label: '12:00 PM' }
];

// Quick preset options for common requests
export const QUICK_PRESETS = [
  {
    label: 'Early (7:00-8:30)',
    type: TIME_REQUEST_TYPES.BETWEEN,
    startTime: TIME_RANGES.EARLY.start,
    endTime: TIME_RANGES.EARLY.end
  },
  {
    label: 'Middle (8:30-10:00)',
    type: TIME_REQUEST_TYPES.BETWEEN,
    startTime: TIME_RANGES.MIDDLE.start,
    endTime: TIME_RANGES.MIDDLE.end
  },
  {
    label: 'Late (After 10:00)',
    type: TIME_REQUEST_TYPES.AFTER,
    time: TIME_RANGES.LATE.start
  }
];

// Helper to convert 24h time string to display format
export function formatTimeForDisplay(time24) {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
}

// Helper to convert display time to 24h format
export function parseTimeToMilitary(timeStr) {
  if (!timeStr) return null;

  // Handle already in 24h format
  if (/^\d{1,2}:\d{2}$/.test(timeStr)) {
    const [h, m] = timeStr.split(':');
    return `${h.padStart(2, '0')}:${m}`;
  }

  // Handle 12h format with AM/PM
  const match = timeStr.match(/(\d{1,2}):?(\d{2})?\s*(AM|PM|am|pm)?/i);
  if (!match) return null;

  let hours = parseInt(match[1], 10);
  const minutes = match[2] ? parseInt(match[2], 10) : 0;
  const period = (match[3] || '').toUpperCase();

  if (period === 'PM' && hours !== 12) hours += 12;
  if (period === 'AM' && hours === 12) hours = 0;

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

// Generate display string for structured time request
export function formatTimeRequestForDisplay(structuredRequest) {
  if (!structuredRequest || structuredRequest.type === TIME_REQUEST_TYPES.NONE) {
    return 'No preference';
  }

  switch (structuredRequest.type) {
    case TIME_REQUEST_TYPES.EXACT:
      return `Exact: ${formatTimeForDisplay(structuredRequest.time)}`;

    case TIME_REQUEST_TYPES.BEFORE:
      return `Before ${formatTimeForDisplay(structuredRequest.time)}`;

    case TIME_REQUEST_TYPES.AFTER:
      return `After ${formatTimeForDisplay(structuredRequest.time)}`;

    case TIME_REQUEST_TYPES.BETWEEN:
      return `${formatTimeForDisplay(structuredRequest.startTime)} - ${formatTimeForDisplay(structuredRequest.endTime)}`;

    default:
      return 'No preference';
  }
}

// Convert legacy string time preference to structured format
export function convertLegacyTimePreference(legacyText) {
  if (!legacyText || typeof legacyText !== 'string') {
    return { type: TIME_REQUEST_TYPES.NONE };
  }

  const lowerText = legacyText.toLowerCase().trim();

  // Check for "early" keywords
  if (/\b(early|first|morning)\b/.test(lowerText) && !/\bafter\b/.test(lowerText)) {
    return {
      type: TIME_REQUEST_TYPES.BETWEEN,
      startTime: TIME_RANGES.EARLY.start,
      endTime: TIME_RANGES.EARLY.end,
      originalText: legacyText
    };
  }

  // Check for "middle" keywords
  if (/\b(middle|mid|midday)\b/.test(lowerText) && !/\b(early|late|after|before)\b/.test(lowerText)) {
    return {
      type: TIME_REQUEST_TYPES.BETWEEN,
      startTime: TIME_RANGES.MIDDLE.start,
      endTime: TIME_RANGES.MIDDLE.end,
      originalText: legacyText
    };
  }

  // Check for "late" keywords
  if (/\b(late|later|last|afternoon)\b/.test(lowerText) && !/\bbefore\b/.test(lowerText)) {
    return {
      type: TIME_REQUEST_TYPES.AFTER,
      time: TIME_RANGES.LATE.start,
      originalText: legacyText
    };
  }

  // Check for "before X" pattern
  const beforeMatch = lowerText.match(/before\s+(\d{1,2}(?::\d{2})?\s*(?:am|pm)?)/i);
  if (beforeMatch) {
    const parsedTime = parseTimeToMilitary(beforeMatch[1]);
    if (parsedTime) {
      return {
        type: TIME_REQUEST_TYPES.BEFORE,
        time: parsedTime,
        originalText: legacyText
      };
    }
  }

  // Check for "after X" pattern
  const afterMatch = lowerText.match(/after\s+(\d{1,2}(?::\d{2})?\s*(?:am|pm)?)/i);
  if (afterMatch) {
    const parsedTime = parseTimeToMilitary(afterMatch[1]);
    if (parsedTime) {
      return {
        type: TIME_REQUEST_TYPES.AFTER,
        time: parsedTime,
        originalText: legacyText
      };
    }
  }

  // Check for specific time pattern
  const exactMatch = lowerText.match(/^(\d{1,2}(?::\d{2})?\s*(?:am|pm)?)$/i);
  if (exactMatch) {
    const parsedTime = parseTimeToMilitary(exactMatch[1]);
    if (parsedTime) {
      return {
        type: TIME_REQUEST_TYPES.EXACT,
        time: parsedTime,
        originalText: legacyText
      };
    }
  }

  // No pattern matched
  return {
    type: TIME_REQUEST_TYPES.NONE,
    originalText: legacyText
  };
}
